/** Approval is independent of whether the attendance is Planned or Modified. */
import { VisitApprovalStandingStatus, VisitBrowseRow } from '@jms/model';
import { defineConditionalTag } from '../../shared/define.conditional.tag';
import type { StatusTone } from '../../shared/status.tag.tones';

type ApprovalStatus = NonNullable<VisitBrowseRow['approvalStatus']>;

const TONES: Record<ApprovalStatus, StatusTone> = {
  [VisitApprovalStandingStatus.Approved]: 'success',
  [VisitApprovalStandingStatus.NeedsReview]: 'warning',
  [VisitApprovalStandingStatus.CheckPending]: 'warning',
  [VisitApprovalStandingStatus.NotApproved]: 'neutral',
  // Distinct from a person's approval (success): the Service Line policy stands in for one.
  [VisitApprovalStandingStatus.AutoApproved]: 'brand',
};

const LABELS: Record<ApprovalStatus, string> = {
  [VisitApprovalStandingStatus.Approved]: 'Approved',
  [VisitApprovalStandingStatus.NeedsReview]: 'Changed since approval',
  [VisitApprovalStandingStatus.CheckPending]: 'Approval check pending',
  [VisitApprovalStandingStatus.NotApproved]: 'Not approved',
  [VisitApprovalStandingStatus.AutoApproved]: 'Auto-approved',
};

/** Once a Job exists the Job is the Visit's record, so an approval validity pill would only be noise. */
export const visitApprovalTag = defineConditionalTag<VisitBrowseRow>(row => {

  const status = row?.approvalStatus;
  if (row?.job?._id || !status || !(status in TONES)) return null;
  return { label: LABELS[status], tone: TONES[status] };
});

/** The quickview names only the standings someone should notice; Approved / Not approved on every Visit was noise. */
const ATTENTION = new Set<ApprovalStatus>([VisitApprovalStandingStatus.AutoApproved, VisitApprovalStandingStatus.NeedsReview]);

export const visitApprovalAttentionTag = defineConditionalTag<VisitBrowseRow>(row => {

  const status = row?.approvalStatus;
  if (row?.job?._id || !status || !ATTENTION.has(status)) return null;
  return { label: LABELS[status], tone: TONES[status] };
});
